import { Button, Popover, Select, Spinner } from "@shopify/polaris"
import React, { useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { GlobalStateContext } from "../providers/GlobalStateProvider.jsx"
import { useAuthenticatedFetch } from "../../hooks/useAuthenticatedFetch.js"
import { useGetProductData } from "../../hooks/useGetProductData.js"
import { useTranslation } from "react-i18next"

const AITaskTag = ({ task, retryTask }) => {
	const [retryPopoverActive, setRetryPopoverActive] = useState(false)
	const { t } = useTranslation()

	const statusColors = {
		queued: { bg: "#ccc", text: "#222" },
		processing: { bg: "#1e40af", text: "#fff" },
		completed: { bg: "#059669", text: "#fff" },
		errored: { bg: "#dc2626", text: "#fff" },
		rate_limited: { bg: "#f8ba04", text: "#fff" },
	}

	const statusText = {
		queued: t("ai.queued"),
		processing: t("ai.processing"),
		completed: t("ai.completed"),
		errored: t("ai.errored"),
		rate_limited: t("ai.rate_limited"),
	}

	const taskNameText = {
		translate: t("ai.translate"),
		reword: t("ai.reword"),
		"create page": t("ai.create_page"),
	}

	const taskInputText = {
		variants: t("ai.variants"),
		description: t("ai.description"),
		title: t("ai.title"),
	}

	const taskName1 = task?.task?.split(":")?.[0]?.split("_")?.[0] || ""
	const taskName2 = task?.task.split(":")?.[0]?.split("_")?.[1] || ""
	const taskInput = task?.task.split(":")?.[1] || ""

	const bgColor = statusColors[task.status]?.bg || statusColors.queued.bg
	const textColor =
		statusColors[task.status]?.text || statusColors.queued.text

	return (
		<div
			key={task.id}
			style={{
				display: "flex",
				borderRadius: "0.5rem",
				background: "#eeeeee",
				textTransform: "capitalize",
				position: "relative",
				width: "fit-content",
			}}
		>
			<span
				style={{
					padding: "0.2rem 0.5rem",
					fontSize: "0.75rem",
					display: "flex",
					alignItems: "center",
					gap: "0.2rem",
				}}
			>
				{taskName1 === "translate" ? (
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="0.8rem"
						height="0.8rem"
						viewBox="0 0 32 32"
					>
						<path
							fill="currentColor"
							d="M27.85 29H30l-6-15h-2.35l-6 15h2.15l1.6-4h6.85zm-7.65-6l2.62-6.56L25.45 23zM18 7V5h-7V2H9v3H2v2h10.74a14.71 14.71 0 0 1-3.19 6.18A13.5 13.5 0 0 1 7.26 9h-2.1a16.47 16.47 0 0 0 3 5.58A16.84 16.84 0 0 1 3 18l.75 1.86A18.47 18.47 0 0 0 9.53 16a16.92 16.92 0 0 0 5.76 3.84L16 18a14.48 14.48 0 0 1-5.12-3.37A17.64 17.64 0 0 0 14.8 7z"
						></path>
					</svg>
				) : taskName1 === "reword" ? (
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="0.8rem"
						height="0.8rem"
						viewBox="0 0 15 15"
					>
						<path
							fill="currentColor"
							fillRule="evenodd"
							d="M13.9.5a.4.4 0 0 0-.8 0v.6h-.6a.4.4 0 0 0 0 .8h.6v.6a.4.4 0 0 0 .8 0v-.6h.6a.4.4 0 0 0 0-.8h-.6V.5Zm-2.046 2.646a.5.5 0 0 1 0 .708l-1 1a.5.5 0 1 1-.707-.708l1-1a.5.5 0 0 1 .707 0Zm-2 2a.5.5 0 0 1 0 .708l-7 7a.5.5 0 1 1-.708-.708l7-7a.5.5 0 0 1 .708 0ZM13.5 5.1c.22 0 .4.18.4.4v.6h.6a.4.4 0 0 1 0 .8h-.6v.6a.4.4 0 0 1-.8 0v-.6h-.6a.4.4 0 0 1 0-.8h.6v-.6c0-.22.18-.4.4-.4ZM8.9.5a.4.4 0 0 0-.8 0v.6h-.6a.4.4 0 1 0 0 .8h.6v.6a.4.4 0 1 0 .8 0v-.6h.6a.4.4 0 0 0 0-.8h-.6V.5Z"
							clipRule="evenodd"
						></path>
					</svg>
				) : null}
				{taskNameText[taskName1] || ""}
				{taskName2
					? ` ${t("common.and")} ${taskNameText[taskName2] || ""}`
					: ""}{" "}
				{taskInputText[taskInput] || ""}
			</span>

			<Popover
				active={retryPopoverActive}
				onClose={() => setRetryPopoverActive(false)}
				activator={
					<div
						onClick={() => {
							if (
								task.status === "completed" ||
								task.status === "rate_limited"
							) {
								setRetryPopoverActive((prev) => !prev)
							}
						}}
						style={{
							padding: `0.2rem ${task.status === "errored" ? "0.6" : "0.5"}rem`,
							fontSize: "0.75rem",
							background: bgColor,
							color: textColor,
							borderRadius: `0rem 0.5rem ${task.status === "errored" ? "0" : "0.5"}rem 0rem`,
							cursor: "pointer",
						}}
					>
						{statusText[task.status] || ""}
					</div>
				}
			>
				<div style={{ padding: "0.2rem 0.5rem" }}>
					<Button
						fullWidth
						onClick={() => {
							retryTask(task.id)
							setRetryPopoverActive(false)
						}}
					>
						{t("ai.retry")}
					</Button>
				</div>
			</Popover>

			{task.status === "errored" ? (
				<div
					style={{
						position: "absolute",
						bottom: "-1.15rem",
						right: "0rem",
						display: "flex",
						alignItems: "center",
						gap: "0.2rem",
						color: "#333",
						background: "#eee",
						borderRadius: "0rem 0rem 0.5rem 0.5rem",
						padding: "0.2rem 0.45rem",
						cursor: "pointer",
						border: "1px solid #ccc",
						borderTop: "none",
					}}
					onClick={() => retryTask(task.id)}
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="0.75rem"
						height="0.75rem"
						viewBox="0 0 24 24"
					>
						<g
							fill="none"
							stroke="currentColor"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2"
						>
							<path d="M19.933 13.041a8 8 0 1 1-9.925-8.788c3.899-1 7.935 1.007 9.425 4.747"></path>
							<path d="M20 4v5h-5"></path>
						</g>
					</svg>
					<span
						style={{
							fontSize: "0.75rem",
							lineHeight: "1",
							fontWeight: "500",
						}}
					>
						{t("ai.retry")}
					</span>
				</div>
			) : (
				""
			)}
		</div>
	)
}

const AITasksListElement = ({ aiTask, retryTask }) => {
	const { state } = useContext(GlobalStateContext)
	const { title, image } = useGetProductData(
		aiTask.forProductId?.includes("gid")
			? aiTask.forProductId
			: `gid://shopify/Product/${aiTask.forProductId}`,
	)
	const { t } = useTranslation()

	const creditsCost = aiTask.tasks.reduce((acc, task) => {
		return acc + task.creditsCost
	}, 0)

	const hasAtLeastOneTaskErrored = aiTask.tasks.some(
		(task) => task.status === "errored",
	)

	const productId = aiTask.forProductId?.match(/\d+/)?.[0] || ""
	const productUrl = productId
		? `https://${state.shop}/admin/products/${productId}`
		: null

	const titleElement = productUrl ? (
		<a
			style={{
				fontSize: "0.9rem",
				fontWeight: "500",
				lineHeight: "1rem",
				color: "#333",
				width: "85%",
				cursor: "pointer",
				textDecoration: "none",
			}}
			href={productUrl}
			target="_blank"
			rel="noreferrer"
		>
			{title || ""}
		</a>
	) : (
		<span
			style={{
				fontSize: "0.9rem",
				fontWeight: "500",
				lineHeight: "1rem",
				color: "#333",
				width: "85%",
			}}
		>
			{title || ""}
		</span>
	)

	return (
		<div
			key={aiTask.id}
			className="shadow"
			style={{
				position: "relative",
				display: "flex",
				gap: "0.6rem",
				background: "#fff",
				border: "1px solid #d4d4d4",
				borderRadius: "1rem",
				padding: "1rem",
				paddingBottom: "2rem",
			}}
		>
			<div
				style={{
					position: "absolute",
					right: "1rem",
					bottom: "0.5rem",
					fontSize: "0.7rem",
					color: "#777",
				}}
			>
				{aiTask.createdAt
					? new Date(aiTask.createdAt).toLocaleString()
					: ""}
			</div>
			<div
				style={{
					position: "absolute",
					left: "1rem",
					bottom: "0.5rem",
					fontSize: "0.7rem",
					color: "#ccc",
				}}
			>
				{aiTask.forProductId?.match(/\d+/)?.[0] || ""}
			</div>

			<div
				style={{ position: "absolute", top: "0.5rem", right: "0.5rem" }}
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "0.2rem",
						background: "#222",
						color: "#fff",
						borderRadius: "1rem",
						padding: "0.2rem 0.5rem",
					}}
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="1rem"
						height="1rem"
						viewBox="0 0 24 24"
					>
						<g fill="none" fillRule="evenodd">
							<path d="M24 0v24H0V0zM12.594 23.258l-.012.002l-.071.035l-.02.004l-.014-.004l-.071-.036c-.01-.003-.019 0-.024.006l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427c-.002-.01-.009-.017-.016-.018m.264-.113l-.014.002l-.184.093l-.01.01l-.003.011l.018.43l.005.012l.008.008l.201.092c.012.004.023 0 .029-.008l.004-.014l-.034-.614c-.003-.012-.01-.02-.02-.022m-.715.002a.023.023 0 0 0-.027.006l-.006.014l-.034.614c0 .012.007.02.017.024l.015-.002l.201-.093l.01-.008l.003-.011l.018-.43l-.003-.012l-.01-.01z"></path>
							<path
								fill="currentColor"
								d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10s10-4.477 10-10S17.523 2 12 2m0 1a9 9 0 0 1-9 9a9 9 0 0 1 9 9a9 9 0 0 1 9-9a9 9 0 0 1-9-9"
							></path>
						</g>
					</svg>
					<span style={{ fontSize: "0.9rem", fontWeight: "500" }}>
						{creditsCost || 0}
					</span>
				</div>
			</div>

			<div
				style={{
					width: "5rem",
					height: "5rem",
					display: "flex",
					flexDirection: "column",
					gap: "0.2rem",
				}}
			>
				{image ? (
					<img
						style={{
							width: "5rem",
							height: "5rem",
							borderRadius: "1rem",
							marginRight: "1rem",
							objectFit: "contain",
						}}
						alt=""
						src={image}
						referrerPolicy="no-referrer"
					/>
				) : (
					<div
						style={{
							width: "5rem",
							height: "5rem",
							borderRadius: "1rem",
							marginRight: "1rem",
							background: "#efefef",
						}}
					></div>
				)}
			</div>

			<div
				style={{
					display: "flex",
					flexDirection: "column",
					gap: "0.5rem",
					flexGrow: "1",
				}}
			>
				{titleElement}

				<div
					style={{
						display: "flex",
						gap: hasAtLeastOneTaskErrored ? "1.2rem" : "0.5rem",
						flexWrap: "wrap",
					}}
				>
					{aiTask.tasks.map((task) => {
						return (
							<AITaskTag
								key={task.id}
								task={task}
								retryTask={retryTask}
							/>
						)
					})}
				</div>
			</div>
		</div>
	)
}

export const AITasksList = ({ demoData }) => {
	const authenticatedFetch = useAuthenticatedFetch()
	const { state, dispatch } = useContext(GlobalStateContext)
	const navigate = useNavigate()
	const { t } = useTranslation()

	const [sortBy, setSortBy] = useState("dateDesc")
	const [loading, setLoading] = useState(false)
	const [aiTasksGrouped, setAiTasksGrouped] = useState([])
	const [nextCursor, setNextCursor] = useState(null)

	const getAiTasks = (sortByValue, cursor) => {
		if (!cursor) setAiTasksGrouped([])
		setLoading(true)

		const cursorQueryString = cursor ? `&cursor=${cursor}` : ""

		authenticatedFetch(
			`/api/ai/getAiTasks?shop=${state.shop}&sortBy=${sortByValue}${cursorQueryString}`,
		)
			.then((res) => res.json())
			.then((json) => {
				let newAiTasksGrouped = []
				const tasksGroupedByProductIds = {}

				json?.aiTasks?.map((aiTask) => {
					if (tasksGroupedByProductIds[aiTask.forProductId]) {
						tasksGroupedByProductIds[
							aiTask.forProductId
						].tasks.push({
							id: aiTask.id,
							status: aiTask.status,
							task: aiTask.task,
							targetLang: aiTask.targetLang,
							creditsCost: aiTask.creditsCost,
						})
					} else {
						tasksGroupedByProductIds[aiTask.forProductId] = {
							id: aiTask.id,
							createdAt: aiTask.createdAt,
							forProductId: aiTask.forProductId,
							tasks: [
								{
									id: aiTask.id,
									status: aiTask.status,
									task: aiTask.task,
									targetLang: aiTask.targetLang,
									creditsCost: aiTask.creditsCost,
								},
							],
						}
					}
				})

				for (const productId in tasksGroupedByProductIds) {
					newAiTasksGrouped.push(tasksGroupedByProductIds[productId])
				}

				newAiTasksGrouped = newAiTasksGrouped.sort((a, b) => {
					if (sortByValue === "dateDesc") {
						return new Date(b.createdAt) - new Date(a.createdAt)
					} else {
						return new Date(a.createdAt) - new Date(b.createdAt)
					}
				})

				if (cursor) {
					setAiTasksGrouped((prev) => [...prev, ...newAiTasksGrouped])
				} else {
					setAiTasksGrouped(newAiTasksGrouped || [])
				}

				setNextCursor(json.nextCursor || null)
			})
			.finally(() => setLoading(false))
	}

	const onSortByChange = (change) => {
		setSortBy(change)
		getAiTasks(change, null)
	}

	const loadMore = (sortByValue, cursor) => {
		getAiTasks(sortByValue, cursor)
	}

	const retryTask = (aiTaskId) => {
		authenticatedFetch("/api/ai/retryAiTask", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				aiTaskId,
			}),
		}).catch((err) => console.error(err))

		setAiTasksGrouped((prev) =>
			prev.map((aiTask) => {
				aiTask.tasks.map((task) => {
					if (task.id === aiTaskId) {
						task.status = "processing"
					}
				})
				return aiTask
			}),
		)
	}

	useEffect(() => {
		if (!demoData) {
			getAiTasks(sortBy, null)
		}
	}, [])

	return (
		<div>
			<div style={{ marginTop: "2rem" }}>
				<Select
					label={t("ai.sort_by")}
					options={[
						{
							label: t("ai.newest_imported_first"),
							value: "dateDesc",
						},
						{
							label: t("ai.oldest_imported_first"),
							value: "dateAsc",
						},
					]}
					value={sortBy}
					onChange={(change) => onSortByChange(change)}
				/>
			</div>

			<div
				style={{
					marginTop: "2rem",
					display: "flex",
					flexDirection: "column",
					gap: "0.8rem",
				}}
			>
				{demoData?.length > 0 ? (
					demoData?.map((aiTask, i) => {
						return (
							<AITasksListElement
								aiTask={aiTask}
								key={`${aiTask.id}${i}`}
							/>
						)
					})
				) : aiTasksGrouped.length > 0 ? (
					aiTasksGrouped.map((aiTask, i) => {
						return (
							<AITasksListElement
								aiTask={aiTask}
								key={`${aiTask.id}${i}`}
								retryTask={retryTask}
							/>
						)
					})
				) : loading ? (
					""
				) : (
					<div
						style={{
							height: "10rem",
							width: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							border: "1px dashed #777",
							background: "#efefef",
							borderRadius: "0.5rem",
						}}
					>
						<span
							style={{
								fontSize: "0.8rem",
								fontWeight: "500",
								color: "#777",
							}}
						>
							{t("ai.no_ai_tasks_found")} {state.shop}
						</span>
					</div>
				)}
			</div>

			{loading && (
				<div
					style={{
						marginTop: "1rem",
						width: "100%",
						display: "flex",
						justifyContent: "center",
					}}
				>
					<Spinner size="large" />
				</div>
			)}

			{!loading && aiTasksGrouped.length > 0 && nextCursor && (
				<div style={{ marginTop: "1rem" }}>
					<Button
						fullWidth
						onClick={() => loadMore(sortBy, nextCursor)}
					>
						{t("common.load_more")}
					</Button>
				</div>
			)}
		</div>
	)
}
